/* Recruiter-ready candidate report: opens a print-ready window (Save as PDF)
   for one report or a batch (one page per student). Pure HTML, no deps. */

const esc = (s) => String(s ?? "—").replace(/&/g, "&amp;").replace(/</g, "&lt;");
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const pct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

function bar(label, value, note) {
  return `<div class="bar"><div class="bl"><span>${esc(label)}</span><b>${esc(value)}${note ? ` <i>${esc(note)}</i>` : ""}</b></div><div class="bt"><div style="width:${pct(value)}%"></div></div></div>`;
}

function page(r, origin) {
  const s = r.student || {}, w = r.wishire || {}, b = w.breakdown || {}, ai = r.ai || null, res = r.resume, l = r.learning || {}, p = r.proctoring || {};
  const tierCls = { A: "ready", B: "mid", C: "work", D: "none" }[w.tier] || "mid";
  const integrity = (x) => x === "flagged" ? "unverified" : x === "minor" ? "verified, minor notes" : "verified";
  return `<section class="page">
  <header class="head">
    <div>
      ${r.college?.logo ? `<img src="${r.college.logo}" alt="">` : ""}
      <div class="org">${esc(r.college?.name || "Wishire")} · Placement cell</div>
      <h1>${esc(s.name)}</h1>
      <div class="sub">${esc([s.course || s.branch, s.branch && s.course ? s.branch : null, s.grad_year ? `Batch ${s.grad_year}` : null, s.cgpa != null ? `CGPA ${s.cgpa}${s.cgpa_verified ? " (verified)" : ""}` : null].filter(Boolean).join(" · "))}</div>
      <div class="sub">${esc([s.email, s.phone, s.city, s.linkedin_url].filter(Boolean).join(" · "))}</div>
      ${s.target_role ? `<div class="sub">Target role: <b>${esc(s.target_role)}</b></div>` : ""}
    </div>
    <div class="score ${tierCls}">
      <div class="n">${esc(w.score)}</div>
      <div class="lab">Wishire Score</div>
      <div class="tier">${esc(w.tier_label)}${w.provisional ? ` · provisional` : ""}</div>
      <div class="conf">${esc(w.confidence)} confidence</div>
    </div>
  </header>

  ${ai ? `<div class="ai"><div class="k">Recruiter summary</div><p>${esc(ai.summary)}</p>${ai.recruiter_line ? `<p class="rl">${esc(ai.recruiter_line)}</p>` : ""}
    <div class="two"><div><div class="k">Strengths</div><ul>${(ai.strengths || []).map((x) => `<li>${esc(x)}</li>`).join("")}</ul></div>
    <div><div class="k">Coachable</div><ul>${(ai.improvements || []).map((x) => `<li>${esc(x)}</li>`).join("")}</ul></div></div></div>` : ""}

  <div class="grid">
    <div>
      <div class="k">Score breakdown</div>
      ${bar("Mock interview", b.interview, w.has?.interview ? "" : "not taken")}
      ${bar("Résumé", b.resume, w.has?.resume ? "" : "not scored")}
      ${bar("Proctored test", b.mock_test, w.has?.mock_test ? "" : "not taken")}
      ${bar("Skills match", b.skills)}
      ${bar("Communication", b.communication)}
      <div class="note">Proctoring: ${p.attempts ? `${p.verified} of ${p.attempts} attempts verified (camera on, face visible, no tab switching)` : "no proctored attempts yet"}.</div>
    </div>
    <div>
      <div class="k">Evidence</div>
      <table>
        <tr><th>What</th><th>When</th><th>Result</th></tr>
        ${(r.interviews || []).map((i) => `<tr><td>Voice mock · ${esc(i.role)}</td><td>${date(i.when)}</td><td><b>${esc(i.score)}</b>/100 · ${esc(i.verdict || "")} <span class="int">${integrity(i.integrity)}</span></td></tr>`).join("")}
        ${(r.tests || []).map((t) => `<tr><td>${esc(t.kind)} · ${esc(t.name)}</td><td>${date(t.when)}</td><td><b>${esc(t.score)}</b>/100${t.band ? ` · ${esc(t.band)}` : ""} <span class="int">${integrity(t.integrity)}</span></td></tr>`).join("")}
        ${res ? `<tr><td>Résumé · ${esc(res.target_role || "")}</td><td></td><td><b>${esc(res.score)}</b>/100 · ATS ${esc(res.ats_score)} ${res.ats_approved ? "<span class='int'>ATS approved</span>" : ""}</td></tr>` : ""}
        ${!(r.interviews || []).length && !(r.tests || []).length && !res ? `<tr><td colspan="3" class="muted">No evidence yet.</td></tr>` : ""}
      </table>
      ${res?.strengths?.length ? `<div class="k mt">Résumé strengths</div><ul class="tight">${res.strengths.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` : ""}
      <div class="k mt">Learning</div>
      <div class="note">${l.courses_completed || 0} course${l.courses_completed === 1 ? "" : "s"} completed · ${l.prep_solved || 0} practice questions${l.prep_streak ? ` · ${l.prep_streak}-day streak` : ""}</div>
      ${(l.certificates || []).length ? `<ul class="tight">${l.certificates.map((c) => `<li>${esc(c.course_title)} <span class="muted">verify: ${origin}/verify/${esc(c.cert_id)}</span></li>`).join("")}</ul>` : ""}
      ${(s.skills || []).length ? `<div class="k mt">Skills on profile</div><div class="note">${esc(s.skills.slice(0, 15).join(", "))}</div>` : ""}
    </div>
  </div>
  <footer>Generated ${date(r.generated_at)} by Wishire for ${esc(r.college?.name || "the placement cell")}. Scores come from AI-evaluated mock interviews, proctored tests and résumé analysis; "verified" means camera on, face visible and no tab switching during the attempt. Share with recruiters only.</footer>
</section>`;
}

export function candidateReportHtml(reports, title = "Candidate report") {
  const list = Array.isArray(reports) ? reports : [reports];
  const origin = window.location.origin;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#1c1917;margin:0;background:#f5f5f4;font-size:12.5px;line-height:1.45}
    .page{background:#fff;width:210mm;min-height:296mm;margin:12px auto;padding:16mm 16mm 12mm;position:relative;page-break-after:always}
    .page:last-child{page-break-after:auto}
    .head{display:flex;justify-content:space-between;gap:20px;border-bottom:3px double #1c1917;padding-bottom:12px}
    .head img{width:44px;height:44px;object-fit:contain;float:left;margin-right:10px}
    .org{font-size:10.5px;text-transform:uppercase;letter-spacing:.08em;color:#57534e} h1{font-size:26px;margin:4px 0 2px}
    .sub{color:#44403c;font-size:12px;margin-top:2px}
    .score{text-align:center;min-width:120px;padding:10px 12px;border:1px solid #a8a29e;border-radius:12px}
    .score .n{font-size:40px;line-height:1} .score .lab{font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;color:#57534e;margin-top:2px}
    .score .tier{font-weight:bold;margin-top:6px} .score .conf{font-size:10px;color:#78716c}
    .score.ready .tier{color:#15803d} .score.mid .tier{color:#b45309} .score.work .tier{color:#c2410c} .score.none .tier{color:#78716c}
    .ai{margin-top:14px;border:1px solid #a8a29e;border-left:4px solid #1c1917;padding:10px 14px;border-radius:4px} .ai p{margin:4px 0}
    .ai .rl{font-style:italic;color:#44403c} .two{display:flex;gap:18px;margin-top:6px} .two>div{flex:1}
    .k{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#57534e;margin-bottom:6px} .k.mt{margin-top:12px}
    .grid{display:grid;grid-template-columns:1fr 1.3fr;gap:22px;margin-top:16px}
    .bar{margin-bottom:8px} .bl{display:flex;justify-content:space-between;font-size:12px} .bl i{font-style:normal;color:#a8a29e;font-size:10.5px}
    .bt{height:6px;background:#e7e5e4;border-radius:3px;overflow:hidden;margin-top:3px} .bt div{height:100%;background:#d97706}
    table{width:100%;border-collapse:collapse;font-size:11.5px} th,td{border-bottom:1px solid #d6d3d1;padding:5px 6px;text-align:left;vertical-align:top}
    th{font-size:9.5px;text-transform:uppercase;letter-spacing:.06em;color:#57534e}
    .int{font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;color:#15803d;margin-left:4px}
    ul{margin:4px 0 0;padding-left:16px} ul.tight li{margin:1px 0} .note{font-size:11.5px;color:#44403c} .muted{color:#a8a29e}
    footer{position:absolute;left:16mm;right:16mm;bottom:8mm;font-size:9.5px;color:#78716c;border-top:1px solid #d6d3d1;padding-top:6px}
    .toolbar{position:fixed;top:0;left:0;right:0;background:#1c1917;color:#fff;padding:8px 14px;font-family:system-ui,sans-serif;font-size:13px;display:flex;gap:12px;align-items:center;z-index:9}
    .toolbar button{background:#fcd34d;color:#1c1917;border:0;border-radius:999px;padding:6px 14px;font-weight:600;cursor:pointer}
    @media print{body{background:#fff} .page{margin:0;width:auto;min-height:auto;padding:12mm 14mm} .toolbar{display:none} footer{position:static;margin-top:16px}}
  </style></head><body>
  <div class="toolbar"><button onclick="window.print()">Save as PDF / Print</button><span>${list.length} report${list.length === 1 ? "" : "s"} · ${esc(title)}</span></div>
  <div style="height:44px"></div>
  ${list.map((r) => page(r, origin)).join("")}
  </body></html>`;
}

export function openCandidateReports(reports, title = "Candidate report") {
  const html = candidateReportHtml(reports, title);
  const w = window.open("", "_blank");
  if (!w) {
    // Pop-up blocked: download the report instead so nobody is stuck.
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    a.download = `${title.replace(/[^\w]+/g, "-").toLowerCase()}.html`;
    a.click();
    return true;
  }
  w.document.open(); w.document.write(html); w.document.close();
  return true;
}
if (typeof window !== "undefined") window.__candidateReportHtml = candidateReportHtml;   // dev/e2e hook
